// src/app/core/config/echarts.config.ts
import { echart_theme } from '@shared/chart/echart-theme';
import {
  BarChart,
  HeatmapChart,
  LineChart,
  PieChart,
  ScatterChart,
  // MapChart,  // ver o registro do mapa do Brasil, comentado no main.ts
} from 'echarts/charts';
import {
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
  // GeoComponent,  // idem
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { LabelLayout, LegacyGridContainLabel } from 'echarts/features';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';

/**
 * Registra no `echarts/core` — a mesma instância que o `provideEchartsCore` entrega à diretiva
 * do ngx-echarts — tudo que a aplicação usa.
 *
 * **Esta lista é o inventário completo.** Até a AE-700 ela não era: o `main.ts` importava o
 * pacote `echarts` inteiro para chamar `registerMap`, e o pacote inteiro registra tudo direto
 * nos módulos compartilhados. Qualquer omissão daqui funcionava por acidente. Duas categorias
 * de omissão vieram à tona quando a importação saiu, e estão marcadas abaixo.
 *
 * Quem acrescentar uma opção nova ao ECharts — um `radar`, um `markPoint`, um `toolbox.feature`
 * ainda não usado — precisa registrar o módulo correspondente aqui. O sintoma de esquecer é um
 * erro do ECharts no console em runtime, não uma falha de build nem de teste: o registro é
 * global para todo o bundle do karma, então um spec pode ficar verde com o módulo faltando (o
 * `echarts.config.spec.ts` explica esse limite).
 *
 * **O renderer em uso é o SVG.** Todo gráfico do produto passa por `DEFAULT_INITOPTS`, que pede
 * `renderer: 'svg'`. O `CanvasRenderer` fica: custa pouco e é o padrão do zrender para qualquer
 * gráfico que venha a ser montado sem `initOpts`.
 *
 * **Vale também para os gráficos da IA.** Até a AE-701 o `ai-host-chart` carregava o pacote
 * completo (`() => import('echarts')`), que o esbuild punha no bundle inicial (~400 kB). Hoje ele
 * usa esta mesma instância, e o que `buildEChartsOptionsFromRaw` monta (barra, linha, pizza,
 * legenda com rolagem e o aviso de "sem dados" via `graphic`) precisa estar aqui — a guarda é
 * `shared/chart/ai/ai-chart-options.render.spec.ts`.
 */
export function registerEcharts() {
  echarts.use([
    GridComponent,
    // Traz o `axisPointer` junto — o tooltip depende dele.
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    MarkLineComponent,
    // AE-729: o `markArea` que pinta as nove células ao fundo do mapa de perspectivas está no
    // código desde que a tela existe, e o módulo dele nunca esteve nesta lista. O ECharts não
    // reclama quando falta um módulo — ele descarta a opção em silêncio —, então as áreas
    // simplesmente não apareciam. Mesma categoria de omissão que a AE-700 trouxe à tona quando o
    // pacote completo saiu do `main.ts`: funcionava por acidente, e parou de funcionar sem aviso.
    MarkAreaComponent,
    VisualMapComponent,
    // AE-700: o `dataZoom` (slider e inside) das barras e dos scatter, e o `toolbox.feature.dataZoom`
    // dos scatter, funcionavam por virem no pacote completo importado pelo `main.ts`.
    DataZoomComponent,
    ToolboxComponent,
    // AE-701: o aviso "Não foi possível renderizar um gráfico" das respostas da IA sem dados é um
    // `graphic`. Funcionava porque o `ai-host-chart` carregava o pacote completo.
    GraphicComponent,
    BarChart,
    LineChart,
    PieChart,
    ScatterChart,
    HeatmapChart,
    // AE-700: mesma história — o `labelLayout: { hideOverlap: true }` do `DEFAULT_CHART` é uma
    // feature opcional, não vem com os charts.
    LabelLayout,
    // No ECharts 6 o `grid.containLabel` virou feature opcional. Sem ela a opção é ignorada e o grid
    // deixa de reservar espaço para os rótulos dos eixos (o aviso sai em `console.log`, não em
    // `warn`). Usado pelo `DEFAULT_CHART`, pelo tema `audaces`, pelo mapa de perspectivas do Sucesso
    // e pelos gráficos da IA. Faltava desde a AE-700: só funcionava depois de algum gráfico da IA
    // carregar o pacote completo na sessão. Achado no smoke da AE-701 em homolog.
    LegacyGridContainLabel,
    SVGRenderer,
    CanvasRenderer,
  ]);

  // Registra o tema (nome + objeto do tema)
  const name = echart_theme['themeName'] ?? 'audaces';
  const themeObject = (echart_theme as any).theme ?? echart_theme; // ThemeOption guarda em .theme
  echarts.registerTheme(name, themeObject);
}
